import { useCallback, useContext, useMemo, useState } from 'react';
import { createBookingDraft, fetchBookingDrafts, updateBookingDraft } from '../api/BookingDraft';
import { uploadQuotation } from '../api/Quotation';
import { fetchQueryById, updateQuery } from '../api/Query';
import { normalizeQuotationRooms } from '../components/QuotationBuilder/quotationRoomOccupancy';
import { getDatePlusDays } from '../utils/date';
import { getQueryStatusValueForLane } from '../utils/queryStatus';
import { generatePdfBlob } from '../utils/pdf';
import { toast } from 'react-toastify';
import { BookingDraftCreateInput, BookingDraftRecord, ShareQuotationInput } from '../components/PackageBuilder/contracts';
import { CreateQueryRoom } from '../types/Query';
import { AuthContext } from '../context/AuthContext';

const FALLBACK_CUSTOMER_NAME = 'Customer';
const FALLBACK_PACKAGE_NAME = 'Package Quotation';
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]/g;
const BOOKING_DETAILS_MARKER = 'bookingDetails=';

const sanitizePdfFilenamePart = (value: string | undefined, fallback: string) => {
  const normalized = String(value || '')
    .split('')
    .map((char) => (char.charCodeAt(0) < 32 ? ' ' : char))
    .join('')
    .replace(INVALID_FILENAME_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized || fallback;
};

const buildQuotationPdfFilename = ({
  clientName,
  packageName,
}: {
  clientName?: string;
  packageName?: string;
}) => {
  const safeClientName = sanitizePdfFilenamePart(clientName, FALLBACK_CUSTOMER_NAME);
  const safePackageName = sanitizePdfFilenamePart(packageName, FALLBACK_PACKAGE_NAME);
  return `${safeClientName}-${safePackageName}.pdf`;
};

const buildQueryRoomsPayload = (hotelRooms: ShareQuotationInput['hotelRooms']): CreateQueryRoom[] => (
  normalizeQuotationRooms(hotelRooms).map((room) => ({
    Adults: Math.max(1, Number(room.adults || 0)),
    Children: Math.max(0, Number(room.children || 0)),
    ChildrenAges: Array.isArray(room.childrenAges)
      ? room.childrenAges
        .slice(0, Math.max(0, Number(room.children || 0)))
        .map((age) => Number(age ?? 0))
      : [],
  }))
);

const getQueryPax = (hotelRooms: ShareQuotationInput['hotelRooms']) => (
  normalizeQuotationRooms(hotelRooms).reduce(
    (sum, room) => sum + Math.max(1, Number(room.adults || 0)) + Math.max(0, Number(room.children || 0)),
    0
  )
);

const asRecords = (value: unknown): Record<string, any>[] => {
  if (Array.isArray(value)) return value.flatMap(asRecords);
  return value && typeof value === 'object' ? [value as Record<string, any>] : [];
};

const firstText = (...values: unknown[]) => {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
};

/**
 * Preserve the operator's quoted selections in the format used by the booking
 * workspace. This is a snapshot, not a supplier reservation.
 */
const buildFulfilmentPlan = ({
  providerRef,
  title,
  travelStartDate,
  travelEndDate,
  totalAmount,
  currency,
}: {
  providerRef: Record<string, unknown>;
  title: string;
  travelStartDate: string;
  travelEndDate: string;
  totalAmount: number;
  currency: string;
}) => {
  const itinerary = asRecords(providerRef.itinerary);
  const fromItinerary = (field: string) => itinerary.flatMap((day) => asRecords(day[field]));
  const hotels = [...asRecords(providerRef.hotels), ...fromItinerary('hotels')].map((hotel, index) => {
    const roomVariant = hotel.room_variant || hotel.roomVariant || asRecords(hotel.room_variants)[0] || asRecords(hotel.roomVariants)[0];
    return {
      componentId: `hotel-${index + 1}`,
      metadata: {
        ...hotel,
        name: firstText(hotel.name, hotel.title, hotel.hotel_name, hotel.hotelName),
        city: firstText(hotel.city, hotel.city_name, hotel.cityName),
        city_name: firstText(hotel.city_name, hotel.cityName, hotel.city),
        hotel_id: firstText(hotel.hotel_id, hotel.hotelId),
        hotel_code: firstText(hotel.hotel_code, hotel.hotelCode, hotel.hotel_id, hotel.hotelId),
        hotel_ref: firstText(hotel.hotel_ref, hotel.hotelRef),
        star_rating: hotel.star_rating ?? hotel.starRating ?? hotel.hotel_rating,
        check_in_date: firstText(hotel.check_in_date, hotel.checkInDate, hotel.checkin_date, hotel.checkinDate),
        check_out_date: firstText(hotel.check_out_date, hotel.checkOutDate, hotel.checkout_date, hotel.checkoutDate),
        room_variant: roomVariant,
        booking_provider: firstText(hotel.booking_provider, hotel.bookingProvider, hotel.supplier),
      },
    };
  });
  const flights = [...asRecords(providerRef.flights), ...fromItinerary('flights')].map((flight, index) => ({
    componentId: `flight-${index + 1}`,
    metadata: {
      ...flight,
      airline: firstText(flight.airline, flight.validatingAirline),
      flight_number: firstText(flight.flight_number, flight.flightNumber),
      departure_time: firstText(flight.departure_time, flight.departureTime),
      arrival_time: firstText(flight.arrival_time, flight.arrivalTime),
      booking_provider: firstText(flight.booking_provider, flight.bookingProvider, flight.supplier),
    },
  }));
  const transfers = [...asRecords(providerRef.transfers), ...fromItinerary('transfers')].map((transfer, index) => ({
    ...transfer,
    componentId: `transfer-${index + 1}`,
    transfer_type: firstText(transfer.transfer_type, transfer.transferType, transfer.type, transfer.name),
    transfer_date: firstText(transfer.transfer_date, transfer.transferDate, transfer.pickupDate, transfer.date),
  }));
  const activities = [...asRecords(providerRef.activities), ...fromItinerary('activities')].map((activity, index) => ({
    ...activity,
    componentId: `activity-${index + 1}`,
    title: firstText(activity.title, activity.name),
    service_date: firstText(activity.service_date, activity.serviceDate, activity.date),
  }));

  return {
    version: 2,
    kind: 'package',
    summary: { title, travelStartDate, travelEndDate, totalAmount: Number(totalAmount || 0), currency: currency || 'INR' },
    components: {
      hotels,
      flights,
      transfers,
      activities,
      visas: asRecords(providerRef.visaDetails || providerRef.visa_details),
      insurance: asRecords(providerRef.insuranceDetails || providerRef.insurance_details),
    },
    ...(providerRef.transferCalculation || providerRef.transfer_calculation
      ? { transfer_calculation: providerRef.transferCalculation || providerRef.transfer_calculation }
      : {}),
  };
};

const upsertPackageLine = (description: string, packageName: string) => {
  const trimmedPackageName = packageName.trim();
  if (!trimmedPackageName) return description.trim();

  const cleanedLines = description
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => !/^package\s*:/i.test(line.trim()));

  const baseDescription = cleanedLines.join('\n').trim();
  return baseDescription
    ? `${baseDescription}\nPackage: ${trimmedPackageName}`
    : `Package: ${trimmedPackageName}`;
};

const updateStructuredBookingDescription = ({
  description,
  packageName,
  rooms,
  pax,
}: {
  description: string;
  packageName: string;
  rooms: CreateQueryRoom[];
  pax: number;
}) => {
  const markerIndex = description.indexOf(BOOKING_DETAILS_MARKER);
  if (markerIndex === -1) return null;

  const prefix = description.slice(0, markerIndex);
  const tail = description.slice(markerIndex + BOOKING_DETAILS_MARKER.length);
  const firstBrace = tail.indexOf('{');
  const lastBrace = tail.lastIndexOf('}');
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) return null;

  const between = tail.slice(0, firstBrace);
  const jsonText = tail.slice(firstBrace, lastBrace + 1);
  const suffix = tail.slice(lastBrace + 1);

  try {
    const parsed = JSON.parse(jsonText);
    const adults = rooms.reduce((sum, room) => sum + Number(room.Adults || 0), 0);
    const children = rooms.reduce((sum, room) => sum + Number(room.Children || 0), 0);
    const nextDescription = {
      ...parsed,
      ...(packageName.trim() ? { packageName: packageName.trim() } : {}),
      passengers: {
        ...(parsed?.passengers || {}),
        adults,
        children,
        total: pax,
        rooms: rooms.map((room) => ({
          adults: Number(room.Adults || 0),
          children: Number(room.Children || 0),
          childrenAges: Array.isArray(room.ChildrenAges) ? room.ChildrenAges : [],
        })),
      },
    };

    return `${prefix}${BOOKING_DETAILS_MARKER}${between}${JSON.stringify(nextDescription)}${suffix}`;
  } catch {
    return null;
  }
};

const buildUpdatedQueryDescription = ({
  currentDescription,
  packageName,
  rooms,
  pax,
}: {
  currentDescription?: string;
  packageName: string;
  rooms: CreateQueryRoom[];
  pax: number;
}) => {
  const description = String(currentDescription || '');
  const structured = updateStructuredBookingDescription({
    description,
    packageName,
    rooms,
    pax,
  });

  if (structured) return structured;
  return upsertPackageLine(description, packageName);
};

const createDraftPayload = ({
  packageId,
  queryId,
  providerRef,
  travelStartDate,
  travelEndDate,
  customerId,
  assignedId,
  createdBy,
  title,
  totalPrice,
  currency,
}: {
  packageId: string;
  queryId: number;
  providerRef: Record<string, unknown>;
  travelStartDate: string;
  travelEndDate: string;
  customerId: number;
  assignedId?: number;
  createdBy?: number;
  title: string;
  totalPrice: number;
  currency: string;
}): BookingDraftCreateInput => ({
  productType: 'package',
  packageId,
  customerId,
  passengers: { adults: 2, children: 0 },
  startAt: travelStartDate || null,
  endAt: travelEndDate || null,
  providerRef,
  fulfilmentPlan: buildFulfilmentPlan({ providerRef, title, travelStartDate, travelEndDate, totalAmount: totalPrice, currency }),
  queryId,
  assignedId,
  createdBy,
});

export const useShareQuotation = ({
  productType = 'package',
  nightsCount,
  queryId,
  startDate,
  hotelRooms,
  totalPrice,
  providerRef,
  pdfRef,
  currency,
  title,
  customerId,
  assignedId,
  createdBy,
  download = false,
  shareDraft = true,
  client,
}: ShareQuotationInput) => {
  const [isSharing, setIsSharing] = useState(false);
  const authContext = useContext(AuthContext) as { user?: { id?: number | string } } | undefined;
  const actingUserId = authContext?.user?.id ? Number(authContext.user.id) : undefined;

  const canShare = useMemo(
    () => Boolean((queryId || download) && startDate && nightsCount >= 0 && Number(totalPrice || 0) >= 0 && !isSharing),
    [download, isSharing, nightsCount, queryId, startDate, totalPrice]
  );

  const share = useCallback(async () => {
    if (!queryId && !download) return;

    setIsSharing(true);
    try {
      const travelStartDate = startDate;
      const travelEndDate = getDatePlusDays(startDate, nightsCount);

      const sourceEl = pdfRef.current;
      const pdfFilename = buildQuotationPdfFilename({
        clientName: client?.name,
        packageName: title,
      });
      const pdfPromise: Promise<Blob | null> = (async () => {
        if (!sourceEl) return null;
        try {
          const blob = await generatePdfBlob(sourceEl, pdfFilename);
          return blob;
        } catch {
          return null;
        }
      })();

      if (shareDraft && queryId) {
        const packageId = String(providerRef?.packageId || '');
        if (productType !== 'package' || !packageId || !customerId) {
          toast.success('Quotation shared. A package-order draft requires a package and customer.');
        } else {
        try {
          const all = await fetchBookingDrafts(queryId);
          const existing = (all || [])
            .filter((draft: BookingDraftRecord) => (
              Number(draft.query_id || draft.queryId || 0) === Number(queryId)
              && (draft.product_type || draft.productType || 'package') === productType
            ))
            .sort((left: BookingDraftRecord, right: BookingDraftRecord) => (
              new Date(right.updated_at || right.created_at || 0).getTime()
              - new Date(left.updated_at || left.created_at || 0).getTime()
            ))[0];

          if (existing) {
            await updateBookingDraft(existing.id, {
              providerRef,
              startAt: travelStartDate || null,
              endAt: travelEndDate || null,
              fulfilmentPlan: buildFulfilmentPlan({ providerRef, title, travelStartDate, travelEndDate, totalAmount: totalPrice, currency }),
            });
            toast.success('Booking draft updated');
          } else {
            const payload = createDraftPayload({
              packageId,
              queryId,
              providerRef,
              travelStartDate,
              travelEndDate,
              customerId: Number(customerId),
              assignedId: actingUserId ?? assignedId,
              createdBy: actingUserId ?? createdBy,
              title,
              totalPrice,
              currency,
            });
            await createBookingDraft(payload);
            toast.success('Booking draft created');
          }
        } catch {
          try {
            const payload = createDraftPayload({
              packageId,
              queryId,
              providerRef,
              travelStartDate,
              travelEndDate,
              customerId: Number(customerId),
              assignedId: actingUserId ?? assignedId,
              createdBy: actingUserId ?? createdBy,
              title,
              totalPrice,
              currency,
            });
            await createBookingDraft(payload);
            toast.success('Booking draft created');
          } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to create booking draft';
            toast.error(message);
          }
        }
        }
      }

      try {
        const pdfBlob = await pdfPromise;
        if (!pdfBlob) return;

        if (download) {
          try {
            const url = URL.createObjectURL(pdfBlob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = pdfFilename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
          } catch {
            // best-effort download
          }

          if (!shareDraft || !queryId) return;
        }

        const formData = new FormData();
        formData.append('type', 'PDF');
        formData.append('amount', String(Number(totalPrice || 0)));
        formData.append('notes', `Quotation shared for query #${queryId}`);
        formData.append('query_id', String(queryId));
        formData.append('queryId', String(queryId));
        formData.append('packageSnapshot', JSON.stringify(providerRef));
        const file = new File([pdfBlob], pdfFilename, { type: 'application/pdf' });
        formData.append('file', file);

        await uploadQuotation(formData);
        if (shareDraft && queryId) {
          try {
            const rooms = buildQueryRoomsPayload(hotelRooms);
            const pax = getQueryPax(hotelRooms);
            let currentDescription = '';

            try {
              const query = await fetchQueryById(queryId);
              currentDescription = String(query?.description || '');
            } catch {
              // best-effort fetch; we can still update the key query fields below
            }

            await updateQuery(queryId, {
              status: getQueryStatusValueForLane('QUOTATION SHARED'),
              query_date: travelStartDate,
              ...(actingUserId ? { assigned_to: actingUserId } : {}),
              rooms,
              description: buildUpdatedQueryDescription({
                currentDescription,
                packageName: title || '',
                rooms,
                pax,
              }),
            });
          } catch (error: unknown) {
            const message = error instanceof Error
              ? error.message
              : 'Quotation uploaded but failed to update query';
            toast.error(message);
          }
        }
        toast.success('Quotation PDF uploaded');
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to upload quotation PDF';
        toast.error(message);
      }
    } finally {
      setIsSharing(false);
    }
  }, [
    assignedId,
    client,
    customerId,
    createdBy,
    currency,
    download,
    hotelRooms,
    shareDraft,
    actingUserId,
    nightsCount,
    pdfRef,
    productType,
    providerRef,
    queryId,
    startDate,
    title,
    totalPrice,
  ]);

  return {
    canShare,
    isSharing,
    share,
  };
};
